import Supercluster from "supercluster";
import React, { useRef, useState, useEffect, useMemo } from "react";
import maplibregl from "maplibre-gl";
import Papa from "papaparse";
import "maplibre-gl/dist/maplibre-gl.css";
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import { Helmet } from "react-helmet";
import { downloadPDF, downloadExcel } from "./exportUtils";

// Define the base styles for different map styles
const baseLayers = {
  Satellite: {
    version: 8,
    sources: {
      satellite: {
        type: "raster",
        tiles: [
          "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
        ],
        tileSize: 256,
      },
      labels: {
        type: "raster",
        tiles: [
          "https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}",
        ],
        tileSize: 256,
      },
    },
    layers: [
      { id: "satellite", type: "raster", source: "satellite" },
      { id: "labels", type: "raster", source: "labels" },
    ],
  },

  Terrain: {
    version: 8,
    sources: {
      terrain: {
        type: "raster",
        tiles: ["https://a.tile.opentopomap.org/{z}/{x}/{y}.png"],
        tileSize: 256,
        attribution:
          "Map data: © OpenStreetMap contributors, SRTM | Tiles: © OpenTopoMap",
      },
    },
    layers: [{ id: "terrain", type: "raster", source: "terrain" }],
  },

  Streets: {
    version: 8,
    sources: {
      osm: {
        type: "raster",
        tiles: ["https://a.tile.openstreetmap.org/{z}/{x}/{y}.png"],
        tileSize: 256,
        attribution: "© OpenStreetMap contributors",
      },
    },
    layers: [{ id: "osm", type: "raster", source: "osm" }],
  },
};

export default function TopUkSchoolsGcseMap() {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [schools, setSchools] = useState([]);
  const [filterCity, setFilterCity] = useState("All");
  const [filterType, setFilterType] = useState("All");
  const [searchName, setSearchName] = useState("");
  const [pupilsMax, setPupilsMax] = useState(4000);
  const [pupilsMin, setPupilsMin] = useState(0);
  const [grade5Min, setGrade5Min] = useState(0); //70
  const [rankMin, setRankMin] = useState(1);
  const [rankMax, setRankMax] = useState();
  const [uniqueCities, setUniqueCities] = useState([]);
  const [uniqueTypes, setUniqueTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showList, setShowList] = useState(true);
  const [showDescription, setShowDescription] = useState(true);
  
  const [filterAgeRange, setFilterAgeRange] = useState("All");
  const [filterGender, setFilterGender] = useState("All");
  const [filterCountry, setFilterCountry] = useState("All");
  const [uniqueCountries, setUniqueCountries] = useState([]);
  const [uniqueAgeRanges, setUniqueAgeRanges] = useState([]);
  const [uniqueGenders, setUniqueGenders] = useState([]);

  const [sortColumn, setSortColumn] = useState("rank"); // default sorting by rank!!!
  const [sortDirection, setSortDirection] = useState("desc"); // ascending or descending!!!
  const [selectedFile, setSelectedFile] = useState("/data/top_500_UK.csv"); //Select fileconst 
  const [renderTrigger, setRenderTrigger] = useState(0);
  const [exportFormat, setExportFormat] = useState('pdf');
  const [showFilters, setShowFilters] = useState(false); //Button hide
  const [showSummary, setShowSummary] = useState(false);

  


  useEffect(() => { // Automatically set default sortColumn based on selected file
    if (selectedFile === "/data/top700schools.csv" || selectedFile === "/data/top_500_UK.csv") {
      setSortColumn("attainment8");
      //setSortColumn("grade5Plus");
    } else {
      setSortColumn("grade5Plus");
    }
  }, [selectedFile]);


  // NEW: Track current map style
  const [mapStyle, setMapStyle] = useState("Satellite");

  const COLORS = {
    total: "#2a9d8f",
    resource: "#e9c46a",
    capital: "#f4a261",
  };
  const inputStyle = {
    width: "100%",
    padding: "6px 8px",
    border: "1px solid #ccc",
    borderRadius: "4px",
    fontSize: "14px",
  };
  // Initialize the map with the selected style
  useEffect(() => {
    if (map.current) return;
    // Initialize with a default style (e.g. Satellite)
    map.current = new maplibregl.Map({
      container: mapContainer.current,
      style: baseLayers["Satellite"],
      center: [-1, 52.5],
      zoom: 6,      
    });
    map.current.markers = []; // Initialize markers array here
    // Optional fullscreen handler
    map.current.on("click", (e) => {
      const clickedElement = e.originalEvent.target;
      // Only toggle fullscreen if background map is clicked
      const isMapCanvas = clickedElement.classList.contains("maplibregl-canvas");

      if (isMapCanvas) {
        const container = map.current.getContainer();
        if (!document.fullscreenElement) {
          container.requestFullscreen().catch((err) =>
            console.error(`Error enabling fullscreen: ${err.message}`)
          );
        } else {
          document.exitFullscreen();
        }
      }
    });
    map.current.addControl(new maplibregl.NavigationControl(), "top-right");
    map.current.on("moveend", () => {
      // Trigger re-render when map changes to update clusters
      setRenderTrigger((t) => t + 1);
    }); 
    return () => {
      if (map.current?.markers) {
        map.current.markers.forEach((m) => m.remove());
        map.current.markers = [];
      }
      map.current?.remove();
      map.current = null;
    };
  }, []); 

  





  // UPDATE map style (live update)
  useEffect(() => {
    if (!map.current) return;
    map.current.setStyle(baseLayers[mapStyle]);
  }, [mapStyle]);

  useEffect(() => {
    setLoading(true);

    Papa.parse(selectedFile, {
      download: true,
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        // First map without rank
        const dataWithoutRank = results.data.map((row) => ({
          name: row["SCHNAME"] || null,
          address: row["ADDRESS"] || null,
          city: row["TOWN"] || null,
          pupilsKS4: Number.isNaN(parseInt(row["TOTPUPS"], 10)) ? null : parseInt(row["TOTPUPS"], 10),
          numboys: Number.isNaN(parseInt(row["NUMBOYS"], 10)) ? null : parseInt(row["NUMBOYS"], 10),
          numgirls: Number.isNaN(parseInt(row["NUMGIRLS"], 10)) ? null : parseInt(row["NUMGIRLS"], 10),
          grade5Plus: Number.isNaN(parseFloat(row["PTL2BASICS_94"])) ? 0 : parseFloat(row["PTL2BASICS_94"]),
          attainment8: Number.isNaN(parseFloat(row["ATT8SCR"])) ? 0 : parseFloat(row["ATT8SCR"]),
          latitude: Number.isNaN(parseFloat(row["Latitude"])) ? null : parseFloat(row["Latitude"]),
          longitude: Number.isNaN(parseFloat(row["Longitude"])) ? null : parseFloat(row["Longitude"]),
          gender: row["EGENDER"] || null,
          ageRange: row["AGERANGE"] || null,
          progress8Score: row["P8PUP"] || null,
          progress8Description: row["P8_BANDING"] || null,
          NFTYPE: row["NFTYPE"] || null,
          country: row["Country"] || (row["ADDRESS"]?.split(",").pop()?.trim() || null),
        }));

        if (selectedFile === "/data/top_500_UK.csv") {
          dataWithoutRank.forEach((school, i) => {
            const row = results.data[i];

            // Helper: convert "z", "c", "x", "SUPP", or empty to null
            const safeParse = (val) => {
              if (!val) return null;
              const cleaned = val.toString().trim().replace("%", "");
              if (["z", "c", "x", "SUPP", "-", "na", "n/a"].includes(cleaned.toLowerCase())) return null;
              const num = parseFloat(cleaned);
              return Number.isNaN(num) ? null : num;
            };

            school.ebacceng_94_total = safeParse(row["ebacceng_94_total"]);
            school.ebacceng_94_percent = safeParse(row["ebacceng_94_percent"]);
            school.ebaccmat_94_total = safeParse(row["ebaccmat_94_total"]);
            school.ebaccmat_94_percent = safeParse(row["ebaccmat_94_percent"]);
            school.ebaccsci_94_total = safeParse(row["ebaccsci_94_total"]);
            school.ebaccsci_94_percent = safeParse(row["ebaccsci_94_percent"]);
            school.ebacchum_94_total = safeParse(row["ebacchum_94_total"]);
            school.ebacchum_94_percent = safeParse(row["ebacchum_94_percent"]);
            school.ebacclan_94_total = safeParse(row["ebacclan_94_total"]);
            school.ebacclan_94_percent = safeParse(row["ebacclan_94_percent"]);
          });
        }


        

        // Filter out schools without a name or valid attainment8
        const validSchools = dataWithoutRank
          .filter(s => s.name && s.attainment8 !== null);

        // Sort by attainment8 descending and assign ranks
        const rankedSchools = validSchools
          .sort((a, b) => b.attainment8 - a.attainment8)
          .map((s, index) => ({
            ...s,
            rank: index + 1, // rank 1 is best
          }));

        // Merge ranked with unranked (for those with missing data)
        const allSchools = dataWithoutRank.map(s => {
          const ranked = rankedSchools.find(r => r.name === s.name);
          return ranked ? ranked : { ...s, rank: null };
        });

        const highestRank = rankedSchools.length;

        setSchools(allSchools);
        console.log([...new Set(allSchools.map((s) => s.country))]); // show log
        setUniqueCities([...new Set(allSchools.map((s) => s.city).filter(Boolean))].sort());
        setUniqueTypes([...new Set(allSchools.map((s) => s.NFTYPE).filter(Boolean))]);
        setUniqueCountries([...new Set(allSchools.map((s) => s.country).filter(Boolean))]);
        setUniqueAgeRanges([...new Set(allSchools.map((s) => s.ageRange).filter(Boolean))].sort());
        setUniqueGenders([...new Set(allSchools.map((s) => s.gender).filter(Boolean))]);
        setRankMax(highestRank); // this is the highest rank
        setLoading(false);
        
      },
      error: (err) => {
        setError("Failed to load CSV data: " + err.message);
        setLoading(false);
      },
    });
  }, [selectedFile]);
  


  const handleFileChange = (e) => {
    const value = e.target.value;
    setSelectedFile(value);

    // Reset all filters
    setFilterCity("All");
    setFilterType("All");
    setFilterCountry("All");
    setFilterAgeRange("All");
    setFilterGender("All");
    setSearchName("");
    setPupilsMin(0);
    setPupilsMax(4000);
    setGrade5Min(0); //70
    setRankMin(1);
    setRankMax(999); // or set to 999 and update later on CSV load
  };




  const sortedAllSchools = [...schools].sort((a, b) => {
    const aValue = a[sortColumn];
    const bValue = b[sortColumn];

    if (aValue === null || aValue === undefined) return 1;
    if (bValue === null || bValue === undefined) return -1;

    if (typeof aValue === 'string') {
      return sortDirection === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    } else {
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }
  });

  const rankedAllSchools = sortedAllSchools.map((school, index) => ({
    ...school,
    rank: index + 1,
  }));

  const filteredSchools = useMemo(() => {
    return rankedAllSchools.filter((school) => {
      if (filterCity !== "All" && school.city !== filterCity) return false;
      if (filterType !== "All" && school.NFTYPE !== filterType) return false;
      if (filterCountry !== "All" && school.country !== filterCountry) return false;
      if (filterAgeRange !== "All" && school.ageRange !== filterAgeRange) return false;
      if (filterGender !== "All" && school.gender !== filterGender) return false;
      if (searchName.trim() !== "" && !school.name?.toLowerCase().includes(searchName.toLowerCase())) return false;
      if (school.pupilsKS4 > pupilsMax || school.pupilsKS4 < pupilsMin) return false;
      if (school.grade5Plus < grade5Min) return false; //min
      if (school.rank < rankMin || school.rank > rankMax) return false;
      return true;
    });
  }, [
    rankedAllSchools,
    filterCity,
    filterType,
    filterCountry,
    filterAgeRange,
    filterGender,
    searchName,
    pupilsMax,
    pupilsMin,
    grade5Min,
    rankMin,
    rankMax,
  ]);

  const resetFilters = () => {
    setSelectedFile("/data/top_500_UK.csv"); // default dataset
    setSearchName("");
    setSortColumn("attainment8");
    setSortDirection("desc");
    setFilterCountry("All");
    setFilterType("All");
    setRankMin(1);
    setRankMax(500);
    setGrade5Min(0);
    setPupilsMin(0);
    setPupilsMax(4000);
    setFilterCity("All");
    setFilterAgeRange("All");
    setFilterGender("All");
  };
  

// !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
  // const filteredSchoolsA = schools.filter((school) => {
  //   if (filterCity !== "All" && school.city !== filterCity) return false;
  //   if (filterType !== "All" && school.NFTYPE !== filterType) return false;
  //   if (filterCountry !== "All" && school.country !== filterCountry) return false;

  //   if (filterAgeRange !== "All" && school.ageRange !== filterAgeRange) return false;
  //   if (filterGender !== "All" && school.gender !== filterGender) return false;
  //   if (
  //     searchName.trim() !== "" &&
  //     !school.name.toLowerCase().includes(searchName.toLowerCase())
  //   )
  //     return false;
  //   if (school.pupilsKS4 > pupilsMax || school.pupilsKS4 < pupilsMin) return false;
  //   if (school.grade5Plus > grade5Max) return false;
  //   if (school.rank < rankMin || school.rank > rankMax) return false;
  //   return true;
  // });
  


  // const sortedSchools = [...filteredSchoolsA].sort((a, b) => {
  //   const aValue = a[sortColumn];
  //   const bValue = b[sortColumn];

  //   if (aValue === null || aValue === undefined) return 1;
  //   if (bValue === null || bValue === undefined) return -1;

  //   if (typeof aValue === 'string') {
  //     return sortDirection === 'asc'
  //       ? aValue.localeCompare(bValue)
  //       : bValue.localeCompare(aValue);
  //   } else {
  //     return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
  //   }
  // });

  // const rankedSchools = sortedSchools.map((school, index) => ({
  //   ...school,
  //   rank1: index + 1,
  // }));
//!!!!!!!!!!!!!!!!!!!!!!!!!
  const filteredSchoolsWithRank = useMemo(() => {
    return filteredSchools.map((school, index) => ({
      ...school,
      filteredRank: index + 1,
    }));
  }, [filteredSchools]);


  const schoolsWithCoords = useMemo(
    () => filteredSchoolsWithRank.filter(
      (s) => !isNaN(s.latitude) && !isNaN(s.longitude)
    ),
    [filteredSchoolsWithRank]
  );

  
  //const numberOfSortedSchools = sortedSchools.length;


  const schoolsMissingCoords = filteredSchools.length - schoolsWithCoords.length;

  


  const geoJsonPoints = useMemo(() => {
    return schoolsWithCoords.map((school, index) => ({
      type: "Feature",
      properties: {
        cluster: false,
        schoolId: index,
        ...school,
      },
      geometry: {
        type: "Point",
        coordinates: [school.longitude, school.latitude],
      },
    }));
  }, [schoolsWithCoords]);

  const supercluster = useMemo(() => {
    return new Supercluster({
      radius: 60,
      maxZoom: 19,
    }).load(geoJsonPoints);
  }, [geoJsonPoints]);

  const handleDownload = () => {
    if (exportFormat === 'pdf') {
      downloadPDF(filteredSchoolsWithRank, "schools.pdf");
    } else if (exportFormat === 'excel') {
      downloadExcel(filteredSchoolsWithRank, "schools.xlsx");
    }
  };

  useEffect(() => {
    if (!map.current) return;

    // Move getMarkerColor inside useEffect to avoid eslint dependency warning
    const getMarkerColor = (rank) => {   
      const maxRank = rankedAllSchools.length;
      if (rank <= maxRank / 4) return "#7e57c2";      // Purple (Top 25%)
      if (rank <= (maxRank / 4) * 2) return "#1565c0"; // Dark Blue
      if (rank <= (maxRank / 4) * 3) return "#4fc3f7"; // Light Blue
      return "#2e7d32";                                // Dark Green
    };

    const bounds = map.current.getBounds();
    const zoom = map.current.getZoom();
    const clusters = supercluster.getClusters(
      [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()],
      Math.round(zoom)
    );

    if (map.current.markers) {
      map.current.markers.forEach((m) => m.remove());
    }
    map.current.markers = [];

    clusters.forEach((feature) => {
      const [longitude, latitude] = feature.geometry.coordinates;

      if (feature.properties.cluster) {
        const count = feature.properties.point_count;

        const el = document.createElement("div");
        el.className = "cluster-marker";

        const size = 30 + count / 10;
        el.style.width = el.style.height = `${size}px`;
        el.innerText = count;

        el.addEventListener("click", () => {
          const expansionZoom = Math.min(
            supercluster.getClusterExpansionZoom(feature.id),
            18
          );
          map.current.easeTo({
            center: [longitude, latitude],
            zoom: expansionZoom,
          });
        });

        const marker = new maplibregl.Marker({ element: el })
          .setLngLat([longitude, latitude])
          .addTo(map.current);

        map.current.markers.push(marker);
      } else {
        const school = feature.properties;
        const typeDisplayNames = {
          IND: "Independent (private school)",
          ACC: "Funded by the government",
        };

        const displayType = typeDisplayNames[school.NFTYPE] || school.NFTYPE;
        const popup = new maplibregl.Popup({ offset: 25 }).setHTML(
          `<strong>${school.name}</strong><br/>
          <b>Original Rank:</b> ${school.rank}<br/>
          <b>Filtered Rank:</b> ${school.filteredRank}<br/>
          <b>Gender:</b> ${school.gender}<br/>
          <b>Age Range:</b> ${school.ageRange}<br/>
          <b>City/Town:</b> ${school.city}<br/>
          <b>Type:</b> ${displayType}<br/>
          <b>Address:</b> ${school.address}<br/>
          <b>Pupils:</b> ${school.pupilsKS4}<br/>
          <b>Grade 5+ %:</b> ${school.grade5Plus}<br/>
          
          <b>GCSE English %:</b> ${school.ebacceng_94_percent ?? 'N/A'}<br/>
          <b>GCSE Mathematics %:</b> ${school.ebaccmat_94_percent ?? 'N/A'}<br/>
          <b>GCSE Science subjects %:</b> ${school.ebaccsci_94_percent ?? 'N/A'}<br/>
          <b>GCSE History or Geography %:</b> ${school.ebacchum_94_percent ?? 'N/A'}<br/>
          <b>GCSE French, Spanish, etc. %:</b> ${school.ebacclan_94_percent ?? 'N/A'}<br/>`  //add 11/05/2025 <b>Progress:</b> ${school.progress8Score} (${school.progress8Description})<br/>
        );

        const marker = new maplibregl.Marker({
          color: getMarkerColor(school.rank), // now safely used here
        })
          .setLngLat([longitude, latitude])
          .setPopup(popup)
          .addTo(map.current);

        map.current.markers.push(marker);
      }
    });
  }, [supercluster, schoolsWithCoords, renderTrigger, rankedAllSchools]);

  const rankColorLegend = [
    { color: "#7e57c2", label: "Top 25%" },
    { color: "#1565c0", label: "Rank 26%-50%" },
    { color: "#4fc3f7", label: "Rank 51%-75%" },
    { color: "#2e7d32", label: "Rank 76%-100%" },
  ];
  const MarkerIcon = ({ color }) => (
    <svg
      width="18"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ marginRight: "10px" }}
    >
      <path
        d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"
        fill={color}
        stroke="#555"
        strokeWidth="1.5"
      />
      <circle cx="12" cy="9" r="3" fill="white" />
    </svg>
  );
  

 

  return (
    
    <div style={{ background: "#f9f9fc", minHeight: "100vh", padding: "20px" }}>
      <div style={{ maxWidth: "1400px", margin: "auto" }}>
        
        <h1
          style={{
            textAlign: "center",
            color: "#264653",
            marginBottom: "1rem",
            fontSize: "1.5rem", 
            fontWeight: "600"
          }}
        >
          Top Schools in the UK (England, Scotland, Wales) and Northern Ireland by GCSE Performance 2024 and 2025 
        </h1>
        <h1
          style={{
            textAlign: "center",
            color: "#264653",
            marginBottom: "1rem",
            fontSize: "1rem", 
            fontWeight: "600"
          }}
        >
          <p className="last-updated">
            Last updated: <strong>{new Date().toLocaleDateString("en-GB")}</strong> (Academic Year 2024/25. Added data for group of subjects)
          </p>
        </h1>
        <div style={{ textAlign: "center", marginBottom: "2rem", padding: "0 10px" }}>
          <p style={{ fontSize: 14, color: COLORS.total, marginBottom: 12 }}>
            Data sourced from&nbsp;
            <a
              href="https://www.gov.uk/search/research-and-statistics"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: COLORS.resource }}
            >
              the UK Department for Education
            </a>.
          </p>
        </div>
        {/* Dataset Selector - Top Aligned */}
        <div style={{ flex: "0 0 250px" }}>
                      <h3 style={{ marginBottom: "0.5rem" }}>Select Schools' Dataset</h3>

                      <select
            id="dataset-select"
            value={selectedFile}
            onChange={handleFileChange}
            style={{
              width: "280px",
              padding: "10px 12px",
              borderRadius: "8px",
              border: "1px solid #1a73e8",
              fontSize: "0.95rem",
              cursor: "pointer",
              backgroundColor: "#ffffff",
              transition: "border 0.2s ease, box-shadow 0.2s ease",
            }}
            onFocus={(e) => e.target.style.boxShadow = "0 0 5px #1a73e8"}
            onBlur={(e) => e.target.style.boxShadow = "none"}
          >
            <option value="/data/top_414_UK.csv" title="Top schools in the UK & NI, Academic Year 2024">
              Best Schools UK & NI 2024
            </option>
            <option value="/data/top700schools.csv" title="Top schools in England only, Academic Year 2024">
              Best Schools England 2024
            </option>
            <option value="/data/top_500_UK.csv" title="Top schools in England, Academic Year 2025 (Government released data 16.10.2025)">
              Best Schools England 2025
            </option>
          </select>
        </div>

              


        

        {/* --- Responsive Layout Container --- */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "20px",
          }}
        >
          {/* --- Left: List of Schools --- */}
          <div
            style={{
              flex: "1",
              width: "100%",
            }}
            className="schools-left"
          >

        

             <h2 style={{ textAlign: "center", color: "#264653", marginBottom: "1rem" }}>
              Top UK Schools List
            </h2>
          {showList && (
            <div
              style={{
                background: "#fff",
                borderRadius: "8px",
                padding: "1rem",
                boxShadow: "0 0 10px rgba(0,0,0,0.1)",
                maxHeight: "600px",
                overflowY: "auto",
              }}
            >
              <button
                onClick={() => setShowList(false)}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "19px",
                  color: COLORS.resource,
                  cursor: "pointer",
                  marginBottom: "0.5rem",
                }}
              >
                Hide list of schools
              </button>

              <div style={{ marginTop: "1rem" }}>
                <label style={{ display: "block", marginBottom: "0.25rem" }}>Search Name:</label>
                <input
                  type="text"
                  placeholder="Search by school name"
                  value={searchName}
                  onChange={(e) => setSearchName(e.target.value)}
                  style={{
                    ...inputStyle,
                    width: "220px",
                    borderRadius: "4px",
                    padding: "4px",
                    boxSizing: "border-box",
                  }} 
                />
              </div><br></br>



              <ul style={{ listStyleType: "none", paddingLeft: 0, margin: 0 }}>
                {filteredSchools
                .sort((a, b) => {
                  let valA = a[sortColumn];
                  let valB = b[sortColumn];

                  // Handle null or undefined
                  if (valA == null) return 1;
                  if (valB == null) return -1;

                  // If the column is string, use localeCompare
                  if (typeof valA === "string" && typeof valB === "string") {
                    return sortDirection === "asc"
                      ? valA.localeCompare(valB)
                      : valB.localeCompare(valA);
                  }

                  // Otherwise, assume number
                  return sortDirection === "asc" ? valA - valB : valB - valA;
                })

                .map((school) => (
                  <li
                    key={school.rank}
                    style={{
                      borderBottom: "1px solid #ddd",
                      padding: "0.5rem 0",
                      fontSize: "19px",
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      if (!map.current) return;
                      map.current.flyTo({
                        center: [school.longitude, school.latitude],
                        zoom: 14,
                      });
                    }}
                  >
                    <strong>{school.rank}. {school.name}</strong><br />
                    <span>{school.city}</span><br />
                    <span>Grade 5+ (A*–C): <b>{school.grade5Plus}%</b></span>
                  </li>

                ))}
              </ul>
            </div>
          )}
          {!showList && (
            <button
              onClick={() => setShowList(true)}
              style={{
                background: "none",
                border: "none",
                fontSize: "19px",
                color: COLORS.resource,
                cursor: "pointer",
                marginTop: "0.5rem",
              }}
            >
              Show list of schools
            </button>
          )}

          <br></br>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '19px' }}>
          <label htmlFor="exportFormat" style={{ fontWeight: '600' }}>
            Export filtered best schools:
          </label>

          <select
            id="exportFormat"
            value={exportFormat}
            onChange={(e) => setExportFormat(e.target.value)}
            style={{ padding: '6px 10px', borderRadius: '4px', border: '1px solid #ccc' }}
          >
            <option value="pdf">PDF</option>
            <option value="excel">Excel</option>
          </select>

          <button
            onClick={handleDownload}
            style={{
              padding: '8px 19px',
              backgroundColor: COLORS.total,
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: '600',
              transition: 'background-color 0.3s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#216b6c')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#298d8f')}
          >
            Download
          </button>
        </div>
        
      </div>
      


      {/* --- Right: Filters + Map --- */}
      <div
        style={{
          flex: "2",
          width: "100%",
        }}
        className="filters-map-right"
      >
        
        {/* <Tippy content={<span style={{ color: COLORS.capital }}> Choose between two views: the top 300 (2023|2024) or 500 (2024|2025) schools in England by Attainment 8 and Grade 5+% GCSE scores, or all 414 schools across the UK (England, Wales and Scotland) and Northern Ireland based on Grade 5+% GCSE results.</span>}
          placement="right"
          arrow={true}
      > */}
            <h2 style={{ textAlign: "center", color: "#264653", marginBottom: "1rem" }}>
              Best UK Schools Map
            </h2>
            
        {/* </Tippy> */}
            
        



        

        {/* UI */}
        





        
        <div>
            <select
            style={inputStyle}
            value={mapStyle}
            onChange={(e) => setMapStyle(e.target.value)}
          >
            <option value="Satellite">Map Style: Satellite</option>
            <option value="Terrain">Map Style: Terrain</option>
            <option value="Streets">Map Style: Streets</option>
          </select>
        </div>


        <div
          style={{
            position: "relative",
            height: "600px",
            borderRadius: "12px",
            overflow: "hidden",
            marginBottom: "1rem",
            boxShadow: "0 0 15px rgba(0,0,0,0.1)",
          }}
        >
          <div
            ref={mapContainer}
            style={{ width: "100%", height: "100%", position: "relative" }}
            aria-label="Map of UK schools"
          >
            {/* Legend goes *inside* the mapContainer */}
            <div
              style={{
                position: "absolute",
                bottom: "5px",
                left: "5px",
                backgroundColor: "rgba(255, 255, 255, 0.7)",
                padding: "10px",
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                maxWidth: "220px",
                fontSize: "16px",
                color: "#333",
                zIndex: 10,
                userSelect: "none",
              }}
            >
              <strong>Rank Color Legend</strong>
              <div style={{ marginTop: "8px" }}>
                {rankColorLegend.map(({ color, label }) => (
                  <div
                    key={label}
                    style={{ display: "flex", alignItems: "center", marginBottom: "6px" }}
                  >
                    <MarkerIcon color={color} />
                    <span>{label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Loading and error indicators still outside, so they don't go fullscreen */}
          {loading && (
            <div
              style={{
                position: "absolute",
                top: 10,
                right: 10,
                backgroundColor: "rgba(255,255,255,0.8)",
                padding: "5px 10px",
                borderRadius: "4px",
                fontSize: "12px",
                color: "#555",
              }}
            >
              Loading data...
            </div>
          )}
          {error && (
            <div
              style={{
                position: "absolute",
                top: 10,
                right: 10,
                backgroundColor: "rgba(255,200,200,0.9)",
                padding: "5px 10px",
                borderRadius: "4px",
                fontSize: "12px",
                color: "#900",
              }}
            >
              {error}
            </div>
          )}
        </div>

        <br></br>
        <button
          onClick={() => setShowFilters((prev) => !prev)}
          style={{
            marginBottom: "1rem",
            padding: "0.5rem 1rem",
            backgroundColor: "#264653",
            color: "white",
            border: "none",
            borderRadius: "8px",
            cursor: "pointer",
            fontWeight: "bold",
          }}
        >
          {showFilters ? "Hide Filters ▲" : "Show Filters ▼"}
        </button>



        

        {showFilters && (
          <div
            style={{
              display: "flex",
              gap: "2rem",
              marginBottom: "1.5rem",
              flexWrap: "wrap",
            }}
          >
            {/* Left Panel */}
            <div style={{ flex: "0 0 250px" }}>
              <h3 style={{ marginBottom: "0.5rem" }}>Select Schools' Dataset</h3>

              <select
                value={selectedFile}
                onChange={handleFileChange}
                style={{
                  width: "220px",
                  marginBottom: "1rem",
                  borderRadius: "4px",
                  padding: "4px",
                  boxSizing: "border-box",
                }}
              >
                <option value="/data/top_414_UK.csv">Best Schools in the UK, 2024</option>
                <option value="/data/top700schools.csv">Best Schools in England, 2024</option>
                <option value="/data/top_500_UK.csv">Best Schools in England, 2025 (last update)</option>
              </select>

              <div style={{ marginTop: "1rem" }}>
                <label style={{ display: "block", marginBottom: "0.25rem" }}>Search Name:</label>
                <input
                  type="text"
                  placeholder="Search by school name"
                  value={searchName}
                  onChange={(e) => setSearchName(e.target.value)}
                  style={{
                    ...inputStyle,
                    width: "220px",
                    borderRadius: "4px",
                    padding: "4px",
                    boxSizing: "border-box",
                  }}
                />
              </div>
              {/* Reset Filters Button */}
              <button
                onClick={resetFilters} // define this function to reset all filter states
                style={{
                  marginTop: "1rem",
                  width: "220px",
                  padding: "6px",
                  borderRadius: "4px",
                  border: "1px solid #ccc",
                  backgroundColor: "#f5f5f5",
                  cursor: "pointer",
                }}
              >
                Reset Filters
              </button>
              {loading && <p>Loading schools...</p>}
              {error && <p style={{ color: "red" }}>{error}</p>}

              <p style={{ marginTop: "1rem" }}>
                <strong>Total best schools loaded:</strong> {filteredSchools.length}
              </p>

              
            </div>

            {/* Right Panel */}
            <div
              style={{
                flex: "1",
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: "1rem",
              }}
            >
              {[
                {
                  label: "Sort By",
                  content: (
                    <div style={{ display: "flex", gap: "0.5rem" }}>
                      <select
                        style={{ ...inputStyle, width: "100%", borderRadius: "4px", padding: "4px", boxSizing: "border-box" }}
                        value={sortColumn}
                        onChange={(e) => setSortColumn(e.target.value)}
                      >
                        {selectedFile === "/data/top700schools.csv" ? (
  <>
    <option 
      value="attainment8" 
      title="Attainment 8 – average score across 8 GCSE subjects"
    >
      Attainment 8
    </option>
    <option 
      value="grade5Plus" 
      title="Percentage of students achieving Grade 5 or above in GCSE English & Maths"
    >
      Grade 5+ %
    </option>
  </>
) : selectedFile === "/data/top_500_UK.csv" ? (
  <>
      <option value="attainment8" title="Attainment 8 – average score across 8 GCSE subjects">Attainment 8</option>
      <option value="grade5Plus" title="Percent of students achieving Grade 5 or above in English & Maths">Grade 5+ %</option>

      <option value="ebacceng_94_percent" title="Percent of students passing GCSE English. Grade 4 or above">English GCSE %</option>
      <option value="ebaccmat_94_percent" title="Percent of students passing GCSE Maths. Grade 4 or above">Maths GCSE %</option>
      <option value="ebaccsci_94_percent" title="Percent of students passing GCSE Science. Grade 4 or above">Science GCSE %</option>
      <option value="ebacchum_94_percent" title="Percent of students passing GCSE History or Geography. Grade 4 or above">Humanities GCSE %</option>
      <option value="ebacclan_94_percent" title="Percent of students passing GCSE foreign language. Grade 4 or above">Language GCSE %</option>
  </>
) : (
  <option 
    value="grade5Plus" 
    title="Percentage of students achieving Grade 5 or above in GCSE English & Maths"
  >
    Grade 5+ %
  </option>
)}

                      </select>
                      <select
                        style={{ ...inputStyle, width: "100%", borderRadius: "4px", padding: "4px", boxSizing: "border-box" }}
                        value={sortDirection}
                        onChange={(e) => setSortDirection(e.target.value)}
                      >
                        <option value="desc">Best to ...</option>
                        <option value="asc">... to best</option>
                      </select>
                    </div>
                  ),
                },
                {
                  label: "Country",
                  content: (
                    <select
                      style={{ ...inputStyle, width: "100%", borderRadius: "4px", padding: "4px", boxSizing: "border-box" }}
                      value={filterCountry}
                      onChange={(e) => setFilterCountry(e.target.value)}
                    >
                      <option value="All">All</option>
                      {uniqueCountries.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  ),
                },
                {
                  label: "Type (Government/Private)",
                  content: (
                    <select
                      style={{ ...inputStyle, width: "100%", borderRadius: "4px", padding: "4px", boxSizing: "border-box" }}
                      value={filterType}
                      onChange={(e) => setFilterType(e.target.value)}
                    >
                      <option value="All">All</option>
                      {uniqueTypes.map((type) => (
                        <option key={type} value={type}>
                          {type === "IND" ? "Independent (private school)" : type === "ACC" ? "Funded by the government" : type}
                        </option>
                      ))}
                    </select>
                  ),
                },
                {
                  label: "Rank (min - max)",
                  content: (
                    <div style={{ display: "flex", gap: "8px" }}>
                      <input
                        type="number"
                        min={1}
                        max={rankMax}
                        value={rankMin}
                        onChange={(e) => setRankMin(Number(e.target.value))}
                        style={{ ...inputStyle, flex: 1, borderRadius: "4px", padding: "4px", boxSizing: "border-box" }}
                      />
                      <input
                        type="number"
                        min={rankMin}
                        max={500}
                        value={rankMax}
                        onChange={(e) => setRankMax(Number(e.target.value))}
                        style={{ ...inputStyle, flex: 1, borderRadius: "4px", padding: "4px", boxSizing: "border-box" }}
                      />
                    </div>
                  ),
                },
                {
                  label: "Grade 5+ % (A*-C)",
                  content: (
                    <>
                      <input
                        type="range"
                        min={0}
                        max={100}
                        step={0.1}
                        value={grade5Min}
                        onChange={(e) => setGrade5Min(Number(e.target.value))}
                        style={{
                          width: "100%",
                          borderRadius: 5,
                          height: "5px",
                          appearance: "none",
                          background: `linear-gradient(to left, #2196f3 ${100 - grade5Min}%, #ddd ${100 - grade5Min}%)`,
                        }}
                      />
                      <div style={{ fontSize: 12 }}>{grade5Min.toFixed(1)} → 100</div>
                    </>
                  ),
                },
                {
                  label: "Pupils (min - max)",
                  content: (
                    <div style={{ display: "flex", gap: "8px" }}>
                      <input
                        type="number"
                        min={0}
                        max={4000}
                        step={50}
                        value={pupilsMin}
                        onChange={(e) => setPupilsMin(Number(e.target.value))}
                        style={{ ...inputStyle, flex: 1, borderRadius: "4px", padding: "4px", boxSizing: "border-box" }}
                      />
                      <input
                        type="number"
                        min={0}
                        max={4000}
                        step={50}
                        value={pupilsMax}
                        onChange={(e) => setPupilsMax(Number(e.target.value))}
                        style={{ ...inputStyle, flex: 1, borderRadius: "4px", padding: "4px", boxSizing: "border-box" }}
                      />
                    </div>
                  ),
                },
                {
                  label: "City/Town",
                  content: (
                    <select
                      style={{ ...inputStyle, width: "100%", borderRadius: "4px", padding: "4px", boxSizing: "border-box" }}
                      value={filterCity}
                      onChange={(e) => setFilterCity(e.target.value)}
                    >
                      <option value="All">All</option>
                      {uniqueCities.map((city) => (
                        <option key={city} value={city}>{city}</option>
                      ))}
                    </select>
                  ),
                },
                {
                  label: "Age Range",
                  content: (
                    <select
                      style={{ ...inputStyle, width: "100%", borderRadius: "4px", padding: "4px", boxSizing: "border-box" }}
                      value={filterAgeRange}
                      onChange={(e) => setFilterAgeRange(e.target.value)}
                    >
                      <option value="All">All</option>
                      {uniqueAgeRanges.map((ar) => (
                        <option key={ar} value={ar}>{ar}</option>
                      ))}
                    </select>
                  ),
                },
                {
                  label: "Gender",
                  content: (
                    <select
                      style={{ ...inputStyle, width: "100%", borderRadius: "4px", padding: "4px", boxSizing: "border-box" }}
                      value={filterGender}
                      onChange={(e) => setFilterGender(e.target.value)}
                    >
                      <option value="All">All</option>
                      {uniqueGenders.map((g) => (
                        <option key={g} value={g}>{g}</option>
                      ))}
                    </select>
                  ),
                },
              ].map((filter, idx) => (
                <div key={idx}>
                  <label style={{ display: "block", marginBottom: "0.25rem" }}>{filter.label}</label>
                  {filter.content}
                </div>
              ))}
            </div>
          </div>
        )}
        

      </div>
    </div>
      {/* --- Responsive styling using media queries --- */}
      <style>
        {`
          /* Desktop layout */
          @media (min-width: 900px) {
            div[style*="flex-direction: column"] {
              flex-direction: row !important;
              align-items: stretch; /* equal heights */
            }
            .schools-left {
              flex: 0 0 33%;
              display: flex;
              flex-direction: column;
            }
            .filters-map-right {
              flex: 0 0 67%;
              display: flex;
              flex-direction: column;
            }
            .schools-left > div,
            .filters-map-right > div {
              max-height: 600px;
            }
          }

          // /* Mobile layout */
          // @media (max-width: 899px) {
          //   .schools-left > div,
          //   .filters-map-right > div {
          //     max-height: 200px !important;
          //     height: 200px !important;
          //   }
          // }
        `}
      </style>

      
        


      <div style={{ marginBottom: "1rem", fontSize: "19px", color: "#333" }}>
  <button
    onClick={() => setShowSummary(prev => !prev)}
    style={{
      marginBottom: "0.5rem",
      padding: "0.4rem 0.8rem",
      backgroundColor: "#264653",
      color: "white",
      border: "none",
      borderRadius: "6px",
      cursor: "pointer",
      fontWeight: "bold",
    }}
  >
    {showSummary ? "Hide Summary ▲" : "Show Summary ▼"}
  </button>

  {showSummary && (
    <>
      <p><strong>{filteredSchools.length}</strong> schools match filters</p>
      {schoolsMissingCoords > 0 && (
        <p><strong>{schoolsMissingCoords}</strong> missing coordinates</p>
      )}
      <p><strong>{schoolsWithCoords.length}</strong> schools shown on the map</p>
      <p>
        <strong>
          <Tippy content={<span style={{ color: COLORS.capital }}>Extends beyond secondary education</span>}>
            <span style={{ color: COLORS.total }}>
              {filteredSchools.reduce((total, s) => total + (s.pupilsKS4 || 0), 0)}
            </span>
          </Tippy>
        </strong>{" "} pupils in displayed schools&nbsp;&nbsp;&nbsp;

        <strong>
          {filteredSchools.reduce((total, s) => total + (s.numgirls || 0), 0)}
        </strong>{" "} girls in displayed schools&nbsp;

        <Tippy content="Compared to the total number of pupils in 'girls' schools, this number may differ due to mixed sixth forms.">
          <span style={{ textDecoration: "underline dotted", cursor: "help" }}>(?)</span>
        </Tippy>&nbsp;&nbsp;

        <strong>
          {filteredSchools.reduce((total, s) => total + (s.numboys || 0), 0)}
        </strong>{" "} boys in displayed schools&nbsp;

        <Tippy content="Compared to the total number of pupils in 'boys' schools, this number may differ due to mixed sixth forms.">
          <span style={{ textDecoration: "underline dotted", cursor: "help" }}>(?)</span>
        </Tippy>
      </p>


                <Helmet>
                  <title>Top UK Schools 2025 | GCSE Rankings & Interactive Map — England, NI, Scotland, Wales</title>
                  <link rel="canonical" href="https://ukschoolrankings.co.uk/" />
                  <meta
                    name="description"
                    content="Explore the top secondary schools across England, Northern Ireland, Scotland, and Wales based on GCSE results. Interactive map and detailed rankings filtered by region, rank, type, and performance."
                  />
                  <meta name="author" content="Denis K" />
                  <meta name="robots" content="index, follow" />

                  {/* Open Graph */}
                  <meta
                    property="og:title"
                    content="Top UK Schools 2025 | GCSE Rankings & Interactive Map — England, NI, Scotland, Wales"
                  />
                  <meta
                    property="og:description"
                    content="Explore the top secondary schools across England, Northern Ireland, Scotland, and Wales based on GCSE results with our interactive map and rankings."
                  />
                  <meta property="og:url" content="https://ukschoolrankings.co.uk/" />
                  <meta property="og:type" content="website" />
                  <meta
                    property="og:image"
                    content="https://ukschoolrankings.co.uk/og-image-top-uk-schools-gcse-map.jpg"
                  />
                  <meta property="og:image:width" content="1200" />
                  <meta property="og:image:height" content="630" />

                  {/* Twitter Card */}
                  <meta name="twitter:card" content="summary_large_image" />
                  <meta
                    name="twitter:title"
                    content="Top UK Schools 2025 | GCSE Rankings & Interactive Map — England, NI, Scotland, Wales"
                  />
                  <meta
                    name="twitter:description"
                    content="Explore the top secondary schools across England, Northern Ireland, Scotland, and Wales based on GCSE results with our interactive map and rankings."
                  />
                  <meta
                    name="twitter:image"
                    content="https://ukschoolrankings.co.uk/og-image-top-uk-schools-gcse-map.jpg"
                  />

                  {/* Structured Data */}
                  <script type="application/ld+json">
                    {JSON.stringify({
                      "@context": "https://schema.org",
                      "@type": "Dataset",
                      "name": "Top UK Schools by Rank and GCSE Results",
                      "description":
                        "Interactive data visualizations and rankings of top UK schools (England, Northern Ireland, Scotland, Wales) based on GCSE results.",
                      "url": "https://ukschoolrankings.co.uk",
                      "creator": {
                        "@type": "Person",
                        "name": "Denis K"
                      },
                      "license": "https://opendatacommons.org/licenses/by/",
                      "keywords": [
                        "top UK schools",
                        "GCSE rankings",
                        "England schools",
                        "Northern Ireland schools",
                        "Scotland schools",
                        "Wales schools"
                      ]
                    })}
                  </script>
                </Helmet>


                {/* The rest of your SchoolsMap JSX here */}





              </>
            )}
          </div>

      {showDescription && (
          <div
            style={{
              background: "#fff",
              borderRadius: "8px",
              padding: "1rem",
              boxShadow: "0 0 10px rgba(0,0,0,0.1)",
              maxHeight: "600px",
              overflowY: "auto",
            }}
          >
            <button
              onClick={() => setShowDescription(false)}
              style={{
                background: "none",
                border: "none",
                fontSize: "19px",
                color: "#264653",
                cursor: "pointer",
                marginBottom: "0.5rem",
              }}
            >
              Hide description
            </button>
            <div style={{ margin: "0 auto" }}>
              <p style={{ textAlign: "center", fontSize: 19, color: "#264653", lineHeight: "1.6" }}>
                Most parents think carefully about their children's education — how to give them the best possible opportunities.
                This service helps simplify that task a little by clearly showing the situation with local schools,
                whether around your current location or in an area you're considering moving to.
                <br /><br />
                I originally created this page for myself and my wife, to better understand the quality of education
                our children are currently receiving — and what other options might be available.
                <br /><br />
                Rank according with "Attainment 8"
                This score is based on how well pupils in a school have performed in up to 8 qualifications, which include English, maths, 3 English Baccalaureateopens in a new window qualifications including sciences, computer science, history, geography and languages, and 3 other additional approved qualificationsopens in a new window.
                <br /><br />
                Grade 5 or above in English & maths GCSEs - "Grade 5+ %:"
                This tells you the percentage of pupils who achieved grade 5 or above in English and maths GCSEsopens in a new window. Reformed GCSEs are graded 1 (low) to 9 (high). Grade 5 is a similar level of achievement to a high grade C or low grade B in the old grading.
                <br /><br />
                If you have noticed any errors or would like information about your school to appear here, please kindly send me an email at iDenKUK@gmail.com. 
              </p>
            </div>
            
          </div>
          
          
        )}
        {!showDescription && (
          <button
            onClick={() => setShowDescription(true)}
            style={{
              background: "none",
              border: "none",
              fontSize: "19px",
              color: "#264653",
              cursor: "pointer",
              marginTop: "0.5rem",
            }}
          >
            Show description
          </button>
        )}    
      </div>
      <div style={{ textAlign: "center", marginBottom: "2rem", padding: "0 10px" }}>
          <p style={{ fontSize: 14, color: COLORS.total, marginBottom: 12 }}>
            Data sourced for Scotland Independent Schools from&nbsp;
            <a
              href="https://www.edinburghnews.scotsman.com/education/15-top-private-schools-scotland-highers-results-students-top-grades-4981340?page=1"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: COLORS.resource }}
            >
              The 15 top private schools in Scotland for Highers results
            </a>.
          </p>
        </div>                  
    </div>
    
        

  );
}
